import { useState, useEffect } from 'react';
import useEventSink from './useEventSink';

const _p = { useEventSink };
export const _private = _p;

/**
 * Tracks whether an export is currently in progress for the given namespace/relation.
 * Returns true from the moment an export is initiated until it succeeds or fails.
 * Used to disable export buttons while a server-side export is running.
 *
 * @param {string} namespace
 * @param {string} relation
 * @returns {boolean} isExporting
 */
export default function useExportingState(namespace, relation) {
    const [subscribe] = _p.useEventSink();
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (!namespace || !relation) return;

        // Subscribe to export start events (no status). Completion events carry a status
        // and are ignored here so this handler doesn't depend on subscription registration
        // order relative to the success/failure handlers below.
        const unsubscribeStart = subscribe(
            { verb: 'export', namespace, relation },
            /**
             * @param {*} _payload
             * @param {{ status?: string }} [eventContext]
             */
            (_payload, eventContext) => {
                if (!eventContext?.status) setIsExporting(true);
            }
        );
        const unsubscribeSuccess = subscribe(
            { verb: 'export', namespace, relation, status: 'success' },
            () => setIsExporting(false)
        );
        const unsubscribeFailure = subscribe(
            { verb: 'export', namespace, relation, status: 'failure' },
            () => setIsExporting(false)
        );

        return () => {
            unsubscribeStart?.();
            unsubscribeSuccess?.();
            unsubscribeFailure?.();
        };
    }, [subscribe, namespace, relation]);

    return isExporting;
}
